/* eslint-disable react-refresh/only-export-components */

import { subWeeks } from "date-fns"
import { Button, ButtonLink } from "design-system/atoms/Button"
import { Label } from "design-system/atoms/Label"
import { Switch } from "design-system/atoms/Switch"
import { Card, CardDescription, CardHeader, CardTitle } from "design-system/molecules/Card"
import { ArrowRight, Info, Signal } from "lucide-react"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { OrganizationTypeEnum } from "schema"
import { useGetSignals } from "../../hooks/useGetSignals"
import { SignalsExplanationPopover } from "./SignalsExplanationPopover"
import { SignalGroup, SignalsList } from "./SignalsList"

export const MAX_VISIBLE_GROUPS = 4
export const SEVERITY_ORDER = { Critical: 0, High: 1, Medium: 2, Low: 3 }

export const Signals = () => {
  const { t } = useTranslation()
  const { data: employee } = useAuthEmployeeQuery()
  const canSeeSignals = employee?.organization.type === OrganizationTypeEnum.Client

  const [viewMoreOpen, setViewMoreOpen] = useState(false)
  const [showSolvedSignals, setShowSolvedSignals] = useState(false)

  const dateRange = useMemo(
    () => ({
      from: subWeeks(new Date(), 2),
      to: new Date(),
    }),
    [],
  )

  const { data: rawSignals, isLoading } = useGetSignals({
    dateRange,
    enabled: canSeeSignals,
  })

  const signalGroups = useMemo(() => {
    if (!rawSignals) {
      return []
    }

    const filteredSignals = showSolvedSignals
      ? rawSignals
      : rawSignals.filter((signal) => !signal.isSolved)

    const sortedSignals = filteredSignals.sort((a, b) => {
      const severityDiff = (SEVERITY_ORDER[a.severity] ?? 99) - (SEVERITY_ORDER[b.severity] ?? 99)
      if (severityDiff !== 0) return severityDiff
      return new Date(a.detectedAt).getTime() - new Date(b.detectedAt).getTime()
    })

    const groupsByKeyAndImpact = sortedSignals.reduce<Record<string, SignalGroup>>(
      (acc, signal) => {
        const groupKey = signal.metricPath.split(".")[0] || signal.metricPath
        const isClusters = groupKey.toLowerCase() === "clusters"

        const groupId = isClusters ? `${groupKey}-${signal.id}` : `${groupKey}-${signal.impact}`

        if (!acc[groupId]) {
          acc[groupId] = {
            groupKey,
            impact: signal.impact,
            signals: [],
          }
        }

        acc[groupId].signals.push(signal)

        return acc
      },
      {},
    )

    return Object.values(groupsByKeyAndImpact)
  }, [rawSignals, showSolvedSignals])

  const showViewMore = signalGroups.length > MAX_VISIBLE_GROUPS

  if (!canSeeSignals) {
    return (
      <Card className="flex flex-col items-center gap-4 p-6 text-center">
        <div className="flex flex-col items-center gap-4">
          <Signal className="size-8 text-chart-primary" />
          <div className="flex flex-col items-center">
            <h3 className="font-medium text-lg">{t("dashboard.signals.unlock.title")}</h3>
            <p className="text-sm text-muted-foreground">
              {t("dashboard.signals.unlock.description")}
            </p>
          </div>
        </div>
        <div className="flex flex-row items-center gap-2">
          <SignalsExplanationPopover buttonProps={{ variant: "secondary" }} />
          <ButtonLink variant="outline" to="/settings/subscription/plans">
            <span>{t("dashboard.signals.unlock.upgradeButton")}</span>{" "}
            <ArrowRight className="size-4" />
          </ButtonLink>
        </div>
      </Card>
    )
  }

  return (
    <Card className="flex flex-col p-6 gap-3">
      <CardHeader className="flex flex-row items-top justify-between gap-2 p-0">
        <div className="flex flex-col gap-0">
          <CardTitle className="font-medium text-lg flex flex-row items-center gap-1">
            <span>{t("dashboard.signals.title")}</span>
            <SignalsExplanationPopover
              side="right"
              trigger={
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Info className="size-4" />
                </Button>
              }
            />
          </CardTitle>
          <CardDescription className="text-sm text-muted-foreground">
            {t("dashboard.signals.description")}
          </CardDescription>
        </div>
        <div
          className="flex flex-row items-center gap-2 h-6"
          onClick={() => setShowSolvedSignals(!showSolvedSignals)}
        >
          <Label className="text-xs text-foreground cursor-default">
            {t("dashboard.signals.viewSolved")}
          </Label>
          <Switch
            id="solved-signals-switch"
            checked={showSolvedSignals}
            onCheckedChange={setShowSolvedSignals}
          />
        </div>
      </CardHeader>
      <SignalsList
        groups={signalGroups}
        sliceCount={viewMoreOpen ? signalGroups.length : MAX_VISIBLE_GROUPS}
        isLoading={isLoading}
        showViewMore={showViewMore}
        onViewMore={() => setViewMoreOpen(!viewMoreOpen)}
        viewMoreOpen={viewMoreOpen}
      />
    </Card>
  )
}
